import { useState, useEffect, useRef, useCallback } from "react";
import type * as type from "@vTypes";
import type { ShiftReturnType } from "./roster-craete/useShiftsQuery";
import type { CopyNurses } from "@/pages/roster-create/Roster_create";
import type { PrevTailData } from "./roster-craete/useRosterPrevTail";
import {
  buildCellViolationMap,
  type CellViolationInfo,
} from "@/services/validation";
import type { ShiftCatalog } from "@/services/shift/shiftCatalog";
import { classifyInboundForGroup } from "@/services/assignment/classifyInbound";
import { getDaysInMonth } from "@/utils/dayUtill";
import { getPrecepteePolicy } from "@/utils/precepteePolicy";
import type { GroupMemberInMonth } from "@/api/nurses";
import { getDailyResignationStatus } from "@/components/feature/nurse-membership/monthlyMembershipStore";
import {
  buildShiftIdToGroupMainLetterMap,
  getDefaultShiftsSortedBySequence,
  getManpowerBucketForResolvedShift,
  mergeShiftsForCellLookup,
  normalizeScheduleCellCode,
  buildShiftLookupMaps,
  resolveShiftFromScheduleCellFromMaps,
} from "@/utils/shift-util";

interface UseRosterValidationProps {
  currentScheduleInfo:
    | { year: number; month: number; version: number }
    | undefined;
  currentScheduleID: string | undefined;
  copyNurses: CopyNurses;
  isDragEditing: boolean;
  manPower_monthly: type.ManPowerMonthly | null;
  manPower_daily?: type.ManPowerDaily | null;
  useMaxManpower?: boolean;
  enableMaxManpowerViolation?: boolean;
  year: number;
  month: number;
  serverConfig?: type.ConfigObject | null;
  defaultShiftCode?: type.availableShifts[]; // shift_gb 기준 그룹핑용
  shiftCatalog?: ShiftCatalog;
  isExpanded?: boolean; // 펼침 상태
  shiftsData: ShiftReturnType;
  disableManpowerValidation?: boolean; // 최소인원 검증 비활성화 (원티드 모달용)
  prevTailData?: PrevTailData | null; // 전월 tail 데이터 (설정 위반 교차월 검증용)
  /** 현재 편집 중인 병동 group_id — inbound 잠금 정책용 */
  currentGroupId?: string | null;
  memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>;
}

type DetailedViolation = {
  type: string;
  nurse_id?: string;
  day?: number;
  next_day?: number;
  start_day?: number;
  end_day?: number;
  off_start_day?: number;
  off_end_day?: number;
  shift?: string;
  shift_row_id?: number;
  shift_id?: string;
  required?: number;
  required_max?: number;
  actual?: number;
  max?: number;
  pattern?: string;
};

function normalizeRosterScheduleValue(value: unknown): string | null {
  const code = normalizeScheduleCellCode(value);
  return code ? code : null;
}

function normalizeShiftsForCellLookup(
  shiftsData: ShiftReturnType | undefined
): type.availableShifts[] | undefined {
  return shiftsData?.map((shift) => ({
    ...shift,
    allday: Boolean(shift.allday),
    auto_schedule: Boolean(shift.auto_schedule),
    start_time: String(shift.start_time ?? ""),
    end_time: String(shift.end_time ?? ""),
  }));
}

/**
 * 프론트에서 기본 인력 검증 수행
 * - manPower_monthly/daily와 실제 배정 인원 비교
 * - 서버 요청 없이 즉시 검증
 */
function validateManpower(
  copyNurses: CopyNurses,
  manPower_monthly: type.ManPowerMonthly | null,
  manPower_daily: type.ManPowerDaily | null | undefined,
  year: number,
  month: number,
  defaultShiftCode?: type.availableShifts[],
  isExpanded?: boolean,
  shiftsData?: ShiftReturnType,
  isUseMid?: boolean,
  useMaxManpower?: boolean,
  enableMaxManpowerViolation = true,
  shiftCatalog?: ShiftCatalog
): {
  violations: string[];
  detailed_violations: DetailedViolation[];
} {
  void isExpanded; // 동적 검증으로 통합, 예외 처리용으로 파라미터 유지
  const violations: string[] = [];
  const detailed_violations: DetailedViolation[] = [];

  if (!copyNurses || copyNurses.length === 0 || !manPower_monthly) {
    return { violations, detailed_violations };
  }

  const daysInMonth = new Date(year, month, 0).getDate();

  // 검증 대상 시프트 코드 = manPower_monthly 키 (D, E, N, M 등 동적)
  const shiftCodesToValidate = Object.keys(manPower_monthly).filter((k) => {
    if (typeof manPower_monthly[k] !== "number") return false;
    if (k.endsWith("_max")) return false;
    // use_mid = false이면 최소인원 검증에서 M 제외
    if (isUseMid === false && k === "M") return false;
    return true;
  });

  if (shiftCodesToValidate.length === 0) {
    return { violations, detailed_violations };
  }

  const mergedShifts =
    shiftCatalog?.shifts ??
    mergeShiftsForCellLookup(
      defaultShiftCode ?? [],
      normalizeShiftsForCellLookup(shiftsData)
    );
  const lookupMaps =
    shiftCatalog?.lookupMaps ?? buildShiftLookupMaps(mergedShifts);
  const validateKeys = new Set(shiftCodesToValidate);
  const shiftIdToGroupMainLetter =
    shiftCatalog?.shiftIdToGroupMainLetter ??
    buildShiftIdToGroupMainLetterMap(
      mergedShifts as Parameters<typeof buildShiftIdToGroupMainLetterMap>[0],
      { includeMid: isUseMid !== false }
    );

  const defaultShiftToCanonical =
    shiftCatalog?.defaultShiftToCanonical ??
    (() => {
      const canonical = new Map<string, { id: number; shift_id: string }>();
      getDefaultShiftsSortedBySequence(
        mergedShifts as Parameters<typeof getDefaultShiftsSortedBySequence>[0],
        { excludeOff: true }
      ).forEach((s) => {
        const ds = s.default_shift;
        if (!ds || canonical.has(ds)) return;
        if (isUseMid === false && ds === "M") return;
        if (s.id != null && Number.isFinite(Number(s.id))) {
          canonical.set(ds, {
            id: Number(s.id),
            shift_id: s.shift_id,
          });
        }
      });
      return canonical;
    })();

  // 일별 배정 인원 (동적: manPower_monthly 키 기준)
  // schedule_ids + buildShiftLookupMaps로 RosterMadeTable과 동일하게 해석
  const dailyCounts = Array(daysInMonth)
    .fill(null)
    .map(() => {
      const counts: Record<string, number> = {};
      shiftCodesToValidate.forEach((code) => (counts[code] = 0));
      return counts;
    });

  copyNurses.forEach((nurse) => {
    if (!nurse.schedule) return;
    const scheduleIds = (nurse as { schedule_ids?: (number | null)[] | null })
      .schedule_ids;

    nurse.schedule.forEach((shift: string | null, dayIndex: number) => {
      if (dayIndex >= daysInMonth || !shift) return;
      const trimmed = shift.trim();
      if (!trimmed || trimmed === "-") return;

      const resolved = resolveShiftFromScheduleCellFromMaps(
        trimmed,
        scheduleIds?.[dayIndex] ?? null,
        lookupMaps
      );
      const bucket =
        shiftCatalog?.getManpowerBucket(resolved, validateKeys) ??
        getManpowerBucketForResolvedShift(
          resolved,
          new Map(shiftIdToGroupMainLetter),
          validateKeys
        );
      if (bucket) {
        dailyCounts[dayIndex][bucket]++;
      }
    });
  });

  // 일별 인력 검증 (manPower_monthly 키 순회)
  for (let day = 0; day < daysInMonth; day++) {
    for (const shiftCode of shiftCodesToValidate) {
      const required =
        manPower_daily?.[shiftCode]?.[day] ?? manPower_monthly[shiftCode];
      const actual = dailyCounts[day][shiftCode] ?? 0;

      if (actual < required) {
        const canonical = defaultShiftToCanonical.get(shiftCode);
        const message = `<span>${day + 1}일</span><span>${shiftCode} 근무 인원 미달 (필요: ${required}, 배정: ${actual})</span>`;
        violations.push(message);
        detailed_violations.push({
          day,
          shift: shiftCode,
          shift_row_id: canonical?.id,
          shift_id: canonical?.shift_id,
          required,
          actual,
          type: "manpower_shortage",
        });
      }

      if (useMaxManpower && enableMaxManpowerViolation) {
        const maxKey = `${shiftCode}_max`;
        const requiredMax = manPower_daily?.[maxKey]?.[day];
        if (
          typeof requiredMax === "number" &&
          requiredMax > 0 &&
          actual > requiredMax
        ) {
          const canonical = defaultShiftToCanonical.get(shiftCode);
          const message = `<span>${day + 1}일</span><span>${shiftCode} 근무 인원 초과 (최대: ${requiredMax}, 배정: ${actual})</span>`;
          violations.push(message);
          detailed_violations.push({
            day,
            shift: shiftCode,
            shift_row_id: canonical?.id,
            shift_id: canonical?.shift_id,
            required_max: requiredMax,
            actual,
            type: "manpower_excess",
          });
        }
      }
    }
  }

  return { violations, detailed_violations };
}

/**
 * Config 기반 검증 규칙 수행 --
 * - max_nig_per_month: 월 나이트 근무횟수 체크
 * - three_seq_nig: N 3연속 허용 여부
 * - two_offs_after_three_nig: N 3회 후 OFF 2일 여부
 * - two_offs_after_two_nig: N 2회 후 OFF 2일 여부
 * - banned_day_after_eve: E 다음 날 D 금지 여부
 * - max_conseq_work: 연속 근무일 수 최대 체크
 */

function validateConfigRules(
  copyNurses: CopyNurses,
  serverConfig: type.ConfigObject | null | undefined,
  year: number,
  month: number,
  defaultShiftCode?: type.availableShifts[],
  shiftsData?: ShiftReturnType,
  prevTailData?: PrevTailData | null,
  memberStatusMap?: ReadonlyMap<string, GroupMemberInMonth>
): {
  violations: string[];
  detailed_violations: DetailedViolation[];
} {
  const violations: string[] = [];
  const detailed_violations: DetailedViolation[] = [];

  if (!copyNurses || copyNurses.length === 0 || !serverConfig) {
    return { violations, detailed_violations };
  }

  const daysInMonth = new Date(year, month, 0).getDate();

  const mergedShifts = mergeShiftsForCellLookup(
    defaultShiftCode ?? [],
    shiftsData
  );
  const lookupMaps = buildShiftLookupMaps(mergedShifts);
  const shiftIdToGroupMainLetter = buildShiftIdToGroupMainLetterMap(
    mergedShifts as Parameters<typeof buildShiftIdToGroupMainLetterMap>[0],
    { includeMid: true }
  );

  type ResolvedShift = (typeof mergedShifts)[number] | undefined;

  // shiftsData에서 type이 "근무"인 shift_id만 Set으로 관리 (raw shift_id 판정용)
  const workShiftIds = new Set<string>();
  // shiftsData에서 type이 "휴무"인 shift_id를 Set으로 관리 (raw shift_id 판정용)
  const offShiftIds = new Set<string>();
  // shiftsData에서 shift_gb가 "나이트"인 shift_id를 Set으로 관리 (raw shift_id 판정용)
  const nightShiftIds = new Set<string>();
  // shiftsData에서 shift_gb가 "데이"인 shift_id를 Set으로 관리 (raw shift_id 판정용)
  const dayShiftIds = new Set<string>();
  // shiftsData에서 shift_gb가 "이브닝"인 shift_id를 Set으로 관리 (raw shift_id 판정용)
  const eveningShiftIds = new Set<string>();
  // default_shift 기준 판정을 위한 Set/Map
  const shiftIdToDefaultMap = new Map<string, string>();
  const workDefaultShifts = new Set<string>();
  const offDefaultShifts = new Set<string>();

  if (mergedShifts.length > 0) {
    mergedShifts.forEach((shift) => {
      if (shift.default_shift) {
        shiftIdToDefaultMap.set(shift.shift_id, shift.default_shift);
      }
      if (shift.type === "근무") {
        workShiftIds.add(shift.shift_id);
        if (shift.default_shift) {
          workDefaultShifts.add(shift.default_shift);
        }
      }
      if (shift.type === "휴무") {
        offShiftIds.add(shift.shift_id);
        if (shift.default_shift) {
          offDefaultShifts.add(shift.default_shift);
        }
      }
      if (shift.shift_gb === "나이트") {
        nightShiftIds.add(shift.shift_id);
      }
      if (shift.shift_gb === "데이") {
        dayShiftIds.add(shift.shift_id);
      }
      if (shift.shift_gb === "이브닝") {
        eveningShiftIds.add(shift.shift_id);
      }
    });
  }

  // schedule 값을 default_shift 기준으로 정규화
  const normalizeShift = (
    shift: string | null,
    resolved?: ResolvedShift
  ): string | null => {
    if (resolved) {
      const fromGroup = shiftIdToGroupMainLetter.get(resolved.shift_id);
      if (fromGroup) return fromGroup;
      if (resolved.default_shift) return resolved.default_shift;
      if (resolved.shift_gb === "데이") return "D";
      if (resolved.shift_gb === "이브닝") return "E";
      if (resolved.shift_gb === "나이트") return "N";
    }
    if (!shift || shift === "-") return null;
    const fromMap = shiftIdToDefaultMap.get(shift);
    if (fromMap) return fromMap;
    // DD/EE/NN/MM/OO 등 반복 코드 fallback
    if (shift.length > 1 && shift.split("").every((ch) => ch === shift[0])) {
      return shift[0];
    }
    return shift;
  };

  // 나이트 근무 여부를 체크하는 헬퍼 함수 (N 또는 shift_gb === "나이트"인 shift_id)
  const isNightShift = (
    shift: string | null,
    resolved?: ResolvedShift
  ): boolean => {
    if (!shift) return false;
    const normalized = normalizeShift(shift, resolved);
    if (normalized === "N") return true;
    if (resolved?.shift_gb === "나이트") return true;
    if (nightShiftIds.size > 0 && nightShiftIds.has(shift)) return true;
    return false;
  };

  // 데이 근무 여부를 체크하는 헬퍼 함수 (D 또는 shift_gb === "데이"인 shift_id)
  const isDayShift = (
    shift: string | null,
    resolved?: ResolvedShift
  ): boolean => {
    if (!shift) return false;
    const normalized = normalizeShift(shift, resolved);
    if (normalized === "D") return true;
    if (resolved?.shift_gb === "데이") return true;
    if (dayShiftIds.size > 0 && dayShiftIds.has(shift)) return true;
    return false;
  };

  // 이브닝 근무 여부를 체크하는 헬퍼 함수 (E 또는 shift_gb === "이브닝"인 shift_id)
  const isEveningShift = (
    shift: string | null,
    resolved?: ResolvedShift
  ): boolean => {
    if (!shift) return false;
    const normalized = normalizeShift(shift, resolved);
    if (normalized === "E") return true;
    if (resolved?.shift_gb === "이브닝") return true;
    if (eveningShiftIds.size > 0 && eveningShiftIds.has(shift)) return true;
    return false;
  };

  // OFF 여부를 체크하는 헬퍼 함수
  // type !== "근무"인 코드면 모두 OFF로 간주
  // 단, 미배정(null/"-")은 OFF로 보지 않음
  const isOffDay = (
    shift: string | null,
    resolved?: ResolvedShift
  ): boolean => {
    if (!shift) return false;
    if (shift === "-") return false;
    if (resolved?.type === "근무") return false;
    if (resolved?.type === "휴무") return true;
    const normalized = normalizeShift(shift, resolved);
    // 근무인 경우 false 반환
    if (normalized && workDefaultShifts.has(normalized)) return false;
    if (workShiftIds.size > 0 && workShiftIds.has(shift)) return false;
    // 그 외는 모두 OFF로 간주
    return true;
  };

  // 말일 근처 예외 처리: 다음 달 데이터를 확인할 수 없으므로 OFF 위반 검증을 스킵
  // 롤백이 필요할 경우 이 함수를 false를 반환하도록 수정하거나, 조건문에서 제거
  const shouldSkipOffValidationAtMonthEnd = (
    currentDay: number,
    requiredOffDays: number,
    daysInMonth: number
  ): boolean => {
    // currentDay부터 시작해서 requiredOffDays만큼의 OFF가 필요한 경우
    // 마지막 OFF 날짜가 이번 달 범위를 넘어가면 검증 스킵
    const lastOffDay = currentDay + requiredOffDays - 1;
    return lastOffDay >= daysInMonth;
  };

  // 모든 규칙을 한 번의 순회로 처리하여 성능 최적화
  copyNurses.forEach((nurse) => {
    const nurseRow = nurse as { nurse_id?: string; id?: string };
    const canonicalNurseId = nurseRow.nurse_id || nurseRow.id;
    if (!nurse.schedule || !canonicalNurseId) return;

    const currentSchedule = nurse.schedule;
    const scheduleIds = (nurse as { schedule_ids?: (number | null)[] | null })
      .schedule_ids;

    // prevNurse/tailDays 조회를 한번만 수행 (기존 IIFE 2개 → 단일 블록)
    const prevNurse = prevTailData?.nurses?.find(
      (n) => String(n.nurse_id) === String(canonicalNurseId)
    );
    const tailDays = (() => {
      if (!prevNurse) return [] as number[];
      const src = prevTailData?.tail_days?.length
        ? prevTailData.tail_days
        : Object.keys(prevNurse.shifts ?? {})
            .map(Number)
            .filter((d) => Number.isFinite(d));
      // UI(row_NurseRosterList) 표시 기준과 동일하게 좌표 오프셋 불일치를 방지
      // - day 값이 비정상(0, 32+)인 경우 필터
      return src
        .map(Number)
        .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31)
        .sort((a, b) => a - b);
    })();
    const currentStartIndex = tailDays.length;
    const tailSchedule: (string | null)[] = prevNurse
      ? tailDays.map((d) => prevNurse.shifts[String(d)] ?? null)
      : [];

    // 제로 카피 가상 접근자: 배열 병합([...tail, ...cur]) 대신 인덱스로 직접 접근
    const tailLen = tailSchedule.length;
    const totalKnownDays = tailLen + currentSchedule.length;
    const getShift = (i: number): string | null => {
      if (i < 0 || i >= totalKnownDays) return null;
      return i < tailLen
        ? tailSchedule[i]
        : normalizeRosterScheduleValue(currentSchedule[i - tailLen]);
    };
    const getResolvedShift = (i: number): ResolvedShift => {
      const shift = getShift(i);
      if (!shift || shift === "-") return undefined;
      if (i < tailLen) {
        return resolveShiftFromScheduleCellFromMaps(shift, null, lookupMaps);
      }
      const currentIndex = i - tailLen;
      return resolveShiftFromScheduleCellFromMaps(
        shift,
        scheduleIds?.[currentIndex] ?? null,
        lookupMaps
      );
    };
    const currentEndIndex = currentStartIndex + daysInMonth - 1;
    const intersectsCurrentMonth = (start: number, end: number) =>
      end >= currentStartIndex && start <= currentEndIndex;
    const toCurrentIndex = (idx: number) => idx - currentStartIndex;
    const nurseId = canonicalNurseId;
    const isResignedOnScheduleIndex = (scheduleIndex: number): boolean => {
      const day = scheduleIndex - currentStartIndex + 1;
      if (day < 1 || day > daysInMonth) return false;

      return (
        getDailyResignationStatus(
          memberStatusMap?.get(nurseId),
          year,
          month,
          day
        ) != null
      );
    };

    // 한 번의 순회로 모든 규칙 체크
    let nightCount = 0;
    let consecutiveWork = 0;
    let currentStart = -1;
    let foundThreeSeqNig = false;

    for (let i = 0; i < totalKnownDays; i++) {
      const shift = getShift(i);
      const prevShift = i > 0 ? getShift(i - 1) : null;
      const nextShift = i < totalKnownDays - 1 ? getShift(i + 1) : null;
      const nextNextShift = i < totalKnownDays - 2 ? getShift(i + 2) : null;
      const nextNextNextShift = i < totalKnownDays - 3 ? getShift(i + 3) : null;
      const nextNextNextNextShift =
        i < totalKnownDays - 4 ? getShift(i + 4) : null;
      const resolvedShift = getResolvedShift(i);
      const resolvedPrevShift = i > 0 ? getResolvedShift(i - 1) : undefined;
      const resolvedNextShift =
        i < totalKnownDays - 1 ? getResolvedShift(i + 1) : undefined;
      const resolvedNextNextShift =
        i < totalKnownDays - 2 ? getResolvedShift(i + 2) : undefined;
      const resolvedNextNextNextShift =
        i < totalKnownDays - 3 ? getResolvedShift(i + 3) : undefined;
      const resolvedNextNextNextNextShift =
        i < totalKnownDays - 4 ? getResolvedShift(i + 4) : undefined;

      // 1. max_nig_per_month: 월 나이트 근무횟수 체크
      // 월 단위 규칙이므로 "현재월" 인덱스만 집계
      if (
        i >= currentStartIndex &&
        i <= currentEndIndex &&
        isNightShift(shift, resolvedShift)
      ) {
        nightCount++;
      }

      // 2. three_seq_nig: N 3연속 허용 여부
      // - true면 3회 이상만 검증 (즉, 4회 이상을 위반으로 잡음)
      // - false면 2회 이상만 검증 (즉, 3회 이상을 위반으로 잡음)
      // three_seq_nig: false일 때 N 3연속을 위반으로 잡음 (3연속 금지이므로)
      if (
        !serverConfig.three_seq_nig &&
        !foundThreeSeqNig &&
        isNightShift(shift, resolvedShift) &&
        isNightShift(nextShift, resolvedNextShift) &&
        isNightShift(nextNextShift, resolvedNextNextShift)
      ) {
        if (intersectsCurrentMonth(i, i + 2)) {
          foundThreeSeqNig = true;
          detailed_violations.push({
            nurse_id: nurseId,
            type: "three_seq_nig",
            start_day: toCurrentIndex(i),
            end_day: toCurrentIndex(i + 2),
          });
        }
      }
      // three_seq_nig: true일 때 N 4회 이상을 위반으로 잡음 (3회는 허용, 4회 이상 금지)
      if (
        serverConfig.three_seq_nig &&
        !foundThreeSeqNig &&
        isNightShift(shift, resolvedShift) &&
        isNightShift(nextShift, resolvedNextShift) &&
        isNightShift(nextNextShift, resolvedNextNextShift) &&
        isNightShift(nextNextNextShift, resolvedNextNextNextShift)
      ) {
        if (intersectsCurrentMonth(i, i + 3)) {
          foundThreeSeqNig = true;
          detailed_violations.push({
            nurse_id: nurseId,
            type: "three_seq_nig",
            start_day: toCurrentIndex(i),
            end_day: toCurrentIndex(i + 3),
          });
        }
      }

      // 3. banned_day_after_eve: ND/ED/NE 패턴 불가능
      // - ED: E 다음 날 D 금지
      // - ND: N 다음 날 D 금지
      // - NE: N 다음 날 E 금지
      if (serverConfig.banned_day_after_eve) {
        // ED 패턴: E 다음 날 D
        if (
          isEveningShift(prevShift, resolvedPrevShift) &&
          isDayShift(shift, resolvedShift)
        ) {
          if (intersectsCurrentMonth(i - 1, i)) {
            detailed_violations.push({
              nurse_id: nurseId,
              type: "banned_day_after_eve",
              day: toCurrentIndex(i - 1), // E 날짜
              next_day: toCurrentIndex(i), // D 날짜
              pattern: "ED",
            });
          }
        }
        // ND 패턴: N 다음 날 D
        if (
          isNightShift(prevShift, resolvedPrevShift) &&
          isDayShift(shift, resolvedShift)
        ) {
          if (intersectsCurrentMonth(i - 1, i)) {
            detailed_violations.push({
              nurse_id: nurseId,
              type: "banned_day_after_eve",
              day: toCurrentIndex(i - 1), // N 날짜
              next_day: toCurrentIndex(i), // D 날짜
              pattern: "ND",
            });
          }
        }
        // NE 패턴: N 다음 날 E
        if (
          isNightShift(prevShift, resolvedPrevShift) &&
          isEveningShift(shift, resolvedShift)
        ) {
          if (intersectsCurrentMonth(i - 1, i)) {
            detailed_violations.push({
              nurse_id: nurseId,
              type: "banned_day_after_eve",
              day: toCurrentIndex(i - 1), // N 날짜
              next_day: toCurrentIndex(i), // E 날짜
              pattern: "NE",
            });
          }
        }
      }

      // 4. max_conseq_work: 연속 근무일 수 최대 체크
      // 기획 변경: shiftsData.type === "근무"인 코드만 근무로 판정
      const normalizedShift = normalizeShift(shift, resolvedShift);
      const isWorkDay =
        !!shift &&
        shift !== "-" &&
        (resolvedShift
          ? resolvedShift.type === "근무"
          : workShiftIds.has(shift) ||
            (!!normalizedShift && workDefaultShifts.has(normalizedShift)));
      if (isWorkDay) {
        if (consecutiveWork === 0) {
          currentStart = i;
        }
        consecutiveWork++;
      } else {
        // 연속 근무가 끊겼을 때 즉시 위반 체크 (모든 위반 구간을 잡음)
        if (consecutiveWork > serverConfig.max_conseq_work) {
          detailed_violations.push({
            nurse_id: nurseId,
            type: "max_conseq_work",
            max: serverConfig.max_conseq_work,
            actual: consecutiveWork,
            // currentStart/i-1은 totalKnownDays 기준 인덱스이므로,
            // UI dayIndex(현재월 1일=0, 전월 tail=음수) 좌표로 변환해야 합니다.
            start_day: toCurrentIndex(currentStart),
            end_day: toCurrentIndex(i - 1),
          });
        }
        consecutiveWork = 0;
        currentStart = -1;
      }

      // 5. two_offs_after_three_nig: N 3회 후 OFF 2일 여부
      // 독립적으로 관리되므로 three_seq_nig 조건 제거
      // 예외: 말일 근처에서는 다음 달 데이터를 확인할 수 없으므로 검증 제외
      // OFF 필요 범위가 i+3 ~ i+4이므로, i+4가 daysInMonth를 넘지 않도록 체크
      if (
        serverConfig.two_offs_after_three_nig &&
        isNightShift(shift, resolvedShift) &&
        isNightShift(nextShift, resolvedNextShift) &&
        isNightShift(nextNextShift, resolvedNextNextShift) &&
        !shouldSkipOffValidationAtMonthEnd(i + 3, 2, totalKnownDays) // 말일 예외처리임
      ) {
        // OFF 2일이 모두 필요한데, 둘 중 하나라도 OFF가 아니면 위반
        // OFF = "O" 또는 type === "휴무"인 shift_id
        if (
          (!isResignedOnScheduleIndex(i + 3) &&
            !isOffDay(nextNextNextShift, resolvedNextNextNextShift)) ||
          (!isResignedOnScheduleIndex(i + 4) &&
            !isOffDay(nextNextNextNextShift, resolvedNextNextNextNextShift))
        ) {
          const recoveryEndIndex = isResignedOnScheduleIndex(i + 4)
            ? i + 3
            : i + 4;
          // N 3회 범위: i ~ i+2, OFF 필요 범위: i+3 ~ i+4
          if (intersectsCurrentMonth(i, recoveryEndIndex)) {
            detailed_violations.push({
              nurse_id: nurseId,
              type: "two_offs_after_three_nig",
              start_day: toCurrentIndex(i), // N 시작일
              end_day: toCurrentIndex(recoveryEndIndex),
              off_start_day: toCurrentIndex(i + 3), // OFF 시작일
              off_end_day: toCurrentIndex(recoveryEndIndex),
            });
          }
        }
      }

      // 6. two_offs_after_two_nig: N 2회 후 OFF 2일 여부
      // two_offs_after_three_nig가 true면 더 강한 규칙이 적용되므로 검증 스킵 (중복 방지)
      // 예외: 말일 근처에서는 다음 달 데이터를 확인할 수 없으므로 검증 제외
      // OFF 필요 범위가 i+2 ~ i+3이므로, i+3이 daysInMonth를 넘지 않도록 체크
      if (
        serverConfig.two_offs_after_two_nig &&
        !serverConfig.two_offs_after_three_nig && // 3회 규칙이 적용되면 2회 규칙은 스킵
        isNightShift(shift, resolvedShift) &&
        isNightShift(nextShift, resolvedNextShift) &&
        !shouldSkipOffValidationAtMonthEnd(i + 2, 2, totalKnownDays) // 말일 근처 예외 처리
      ) {
        // OFF 2일이 모두 필요한데, 둘 중 하나라도 OFF가 아니면 위반
        // OFF = "O" 또는 type === "휴무"인 shift_id
        if (
          (!isResignedOnScheduleIndex(i + 2) &&
            !isOffDay(nextNextShift, resolvedNextNextShift)) ||
          (!isResignedOnScheduleIndex(i + 3) &&
            !isOffDay(nextNextNextShift, resolvedNextNextNextShift))
        ) {
          const recoveryEndIndex = isResignedOnScheduleIndex(i + 3)
            ? i + 2
            : i + 3;
          // N 2회 범위: i ~ i+1, OFF 필요 범위: i+2 ~ i+3
          if (intersectsCurrentMonth(i, recoveryEndIndex)) {
            detailed_violations.push({
              nurse_id: nurseId,
              type: "two_offs_after_two_nig",
              start_day: toCurrentIndex(i), // N 시작일
              end_day: toCurrentIndex(recoveryEndIndex),
              off_start_day: toCurrentIndex(i + 2), // OFF 시작일
              off_end_day: toCurrentIndex(recoveryEndIndex),
            });
          }
        }
      }
    }

    // max_nig_per_month 위반 체크 (설정값이 유효한 경우에만)
    if (
      serverConfig.max_nig_per_month > 0 &&
      nightCount > serverConfig.max_nig_per_month
    ) {
      detailed_violations.push({
        nurse_id: nurseId,
        type: "max_nig_per_month",
        max: serverConfig.max_nig_per_month,
        actual: nightCount,
      });
    }

    // max_conseq_work: 월말에 끝나는 연속 근무 구간 체크
    if (consecutiveWork > serverConfig.max_conseq_work) {
      detailed_violations.push({
        nurse_id: nurseId,
        type: "max_conseq_work",
        max: serverConfig.max_conseq_work,
        actual: consecutiveWork,
        // currentStart는 totalKnownDays 기준 인덱스이므로 변환합니다.
        start_day: toCurrentIndex(currentStart),
        end_day: toCurrentIndex(totalKnownDays - 1),
      });
    }
  });

  // 메시지는 나중에 한 번에 생성 (성능 최적화)
  // Map을 사용하여 O(1) 조회로 개선 (O(n²) → O(n))
  const nurseMap = new Map(copyNurses.map((nurse) => [nurse.nurse_id, nurse]));
  const globalTailDays = (() => {
    if (!prevTailData) return [] as number[];
    const payloadTailDays = prevTailData.tail_days;
    if (Array.isArray(payloadTailDays) && payloadTailDays.length > 0) {
      return payloadTailDays
        .map(Number)
        .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31)
        .sort((a, b) => a - b);
    }
    const firstNurse = prevTailData.nurses?.[0];
    if (firstNurse?.shifts) {
      return Object.keys(firstNurse.shifts)
        .map(Number)
        .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31)
        .sort((a, b) => a - b);
    }
    return [] as number[];
  })();

  const dayLabel = (idx?: number) => {
    if (typeof idx !== "number") return "-";
    if (idx < 0) {
      const tailIndex = globalTailDays.length + idx; // tailIndex = 3 + (-1) = 2
      const tailDate = globalTailDays[tailIndex];
      return tailDate ? `전월 ${tailDate}일` : `전월 ${Math.abs(idx)}일전`;
    }
    return `${idx + 1}일`;
  };

  detailed_violations.forEach((v) => {
    const nurseId = v.nurse_id ?? "";
    const nurse = nurseMap.get(nurseId);
    const nurseName = nurse?.name || nurseId;

    switch (v.type) {
      case "max_nig_per_month":
        violations.push(
          `<span>${nurseName}</span><span>월 나이트 근무 초과 (최대: ${v.max}회, 현재: ${v.actual}회)</span>`
        );
        break;
      case "three_seq_nig":
        violations.push(
          `<span>${nurseName}</span><span>N 3연속 근무 위반 (${dayLabel(v.start_day)}~${dayLabel(v.end_day)})</span>`
        );
        break;
      case "banned_day_after_eve": {
        const patternName =
          v.pattern === "ED"
            ? "E 다음 날 D"
            : v.pattern === "ND"
              ? "N 다음 날 D"
              : v.pattern === "NE"
                ? "N 다음 날 E"
                : "E 다음 날 D";
        violations.push(
          `<span>${nurseName}</span><span>${patternName} 근무 위반 (${dayLabel(v.day)} → ${dayLabel(v.next_day)})</span>`
        );
        break;
      }
      case "max_conseq_work":
        violations.push(
          `<span>${nurseName}</span><span>연속 근무일 초과 (최대: ${v.max}일, 현재: ${v.actual}일)</span>`
        );
        break;
      case "two_offs_after_three_nig": {
        const offStart =
          v.off_start_day ??
          (typeof v.start_day === "number" ? v.start_day + 3 : undefined);
        const offEnd =
          v.off_end_day ??
          (typeof v.start_day === "number" ? v.start_day + 4 : undefined);
        const offRange =
          offStart === offEnd
            ? dayLabel(offStart)
            : `${dayLabel(offStart)}~${dayLabel(offEnd)}`;
        violations.push(
          `<span>${nurseName}</span><span>N 3회 후 OFF 미준수 (${dayLabel(v.start_day)}~${dayLabel(typeof v.start_day === "number" ? v.start_day + 2 : undefined)} N 후 ${offRange} OFF 필요)</span>`
        );
        break;
      }
      case "two_offs_after_two_nig": {
        const offStart =
          v.off_start_day ??
          (typeof v.start_day === "number" ? v.start_day + 2 : undefined);
        const offEnd =
          v.off_end_day ??
          (typeof v.start_day === "number" ? v.start_day + 3 : undefined);
        const offRange =
          offStart === offEnd
            ? dayLabel(offStart)
            : `${dayLabel(offStart)}~${dayLabel(offEnd)}`;
        violations.push(
          `<span>${nurseName}</span><span>N 2회 후 OFF 미준수 (${dayLabel(v.start_day)}~${dayLabel(typeof v.start_day === "number" ? v.start_day + 1 : undefined)} N 후 ${offRange} OFF 필요)</span>`
        );
        break;
      }
    }
  });

  return { violations, detailed_violations };
}

// ValidateListType
export type ValidateListType = {
  detailed_violations: DetailedViolation[];
  violations: string[];
};

// ReturnType
export type ValidationPhase =
  | "idle"
  | "waiting_input"
  | "scheduled"
  | "running"
  | "done";

type ReturnType = {
  validateList: ValidateListType | null;
  cellViolationMap: Map<string, CellViolationInfo>; // 셀별 위반 정보 (O(1) lookup)
  isValidating: boolean;
  phase: ValidationPhase;
  validate: () => void; // 외부에서 명시적으로 검증을 트리거하는 함수
};

export function useRosterValidation({
  currentScheduleInfo,
  currentScheduleID,
  copyNurses,
  isDragEditing,
  manPower_monthly,
  manPower_daily,
  useMaxManpower = false,
  enableMaxManpowerViolation = true,
  year,
  month,
  serverConfig,
  defaultShiftCode,
  isExpanded,
  shiftsData,
  shiftCatalog,
  disableManpowerValidation = false,
  prevTailData,
  currentGroupId,
  memberStatusMap,
}: UseRosterValidationProps): ReturnType {
  const [validateList, setValidateList] = useState<ValidateListType | null>(
    null
  );
  const [cellViolationMap, setCellViolationMap] = useState<
    Map<string, CellViolationInfo>
  >(new Map());
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [phase, setPhase] = useState<ValidationPhase>("idle");
  const prevValidationKey = useRef<string>("");
  const prevScheduleId = useRef<string | undefined>(undefined);
  const prevDateKey = useRef<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // 디바운싱용
  const validationRunIdRef = useRef(0);
  const isExpandedRef = useRef(isExpanded); // isExpanded를 ref로 관리하여 함수 재생성 방지

  // 로스터 내용 해시 생성 함수 (순서 무관 비교용)
  const generateRosterHash = useCallback((nurses: CopyNurses) => {
    if (!nurses) return "";
    // nurse_id와 schedule만 추출하여 nurse_id 기준으로 정렬
    const simplified = nurses.map((n) => ({
      id: n.nurse_id,
      schedule: n.schedule,
    }));
    simplified.sort((a, b) => (a.id > b.id ? 1 : -1));
    return JSON.stringify(simplified);
  }, []);

  const clearDebounceTimer = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  // isExpanded ref 업데이트
  useEffect(() => {
    isExpandedRef.current = isExpanded ?? false;
  }, [isExpanded]);

  // 검증 실행 함수 (외부에서 호출 가능)
  const executeValidation = useCallback(() => {
    // null 체크: 데이터가 없으면 즉시 검증 결과 초기화
    // disableManpowerValidation이 true면 manPower_monthly 체크 건너뛰기
    if (
      !currentScheduleInfo ||
      !copyNurses ||
      (!disableManpowerValidation && !manPower_monthly)
    ) {
      clearDebounceTimer();
      validationRunIdRef.current += 1;
      prevValidationKey.current = "";
      setValidateList(null);
      setCellViolationMap((prev) => (prev.size === 0 ? prev : new Map()));
      setIsValidating(false);
      setPhase("waiting_input");
      return;
    }

    // 원티드 모달처럼 최소인원 검증을 끈 상태에서 간호사 목록이 비어 있으면
    // 로딩 중이거나 데이터가 없을 때는 "검증 대기" 상태로 유지
    if (copyNurses.length === 0) {
      clearDebounceTimer();
      validationRunIdRef.current += 1;
      prevValidationKey.current = "";
      setValidateList(null);
      setCellViolationMap((prev) => (prev.size === 0 ? prev : new Map()));
      setIsValidating(false);
      setPhase("waiting_input"); // 항상 "waiting_input" 상태로 설정
      return;
    }

    // 드래그 중일 때는 검증 스킵 (드래그 종료 후 검증)
    if (isDragEditing) {
      clearDebounceTimer();
      validationRunIdRef.current += 1;
      setIsValidating(false);
      setPhase("idle");
      return;
    }

    // 스케줄이 유효한지 확인
    const hasValidSchedule = copyNurses.some((nurse) =>
      nurse.schedule?.some((cell) => {
        const code = normalizeRosterScheduleValue(cell);
        return code !== null && code !== "-" && code !== "";
      })
    );

    if (!hasValidSchedule) {
      clearDebounceTimer();
      validationRunIdRef.current += 1;
      prevValidationKey.current = "";
      // 원티드 모달(최소인원 검증 비활성화)에서는 빈 결과를 명시적으로 반환해
      // "검증 대기"가 아닌 "위반사항 없음" 상태로 표시한다.
      setValidateList((prev) => {
        if (disableManpowerValidation) {
          return prev && prev.violations?.length === 0
            ? prev
            : { violations: [], detailed_violations: [] };
        }
        return null;
      });
      setCellViolationMap((prev) => (prev.size === 0 ? prev : new Map()));
      setIsValidating(false);
      setPhase(disableManpowerValidation ? "done" : "waiting_input");
      return;
    }

    // 검증 트리거 키 생성: 이 값이 바뀔 때만 재검증
    const currentHash = generateRosterHash(copyNurses);
    const validationKey = JSON.stringify({
      scheduleId: currentScheduleID ?? "",
      dateKey: `${year}-${month}`,
      rosterHash: currentHash,
      manPowerHash: disableManpowerValidation
        ? "disabled"
        : JSON.stringify({
            monthly: manPower_monthly,
            daily: manPower_daily,
            useMaxManpower,
            enableMaxManpowerViolation,
          }),
      configHash: JSON.stringify(serverConfig ?? null),
      isExpanded: !!isExpandedRef.current, // ref를 사용하여 함수 재생성 방지
      disableManpowerValidation,
      // shiftsData 포함: 로드 완료 시 M 등 매핑 반영하여 재검증 (연속근무는 즉시 되는데 최소인원은 매핑 필요)
      shiftsDataHash: shiftsData?.length
        ? shiftsData
            .map(
              (s) =>
                `${s.id ?? ""}:${s.shift_id}:${s.default_shift}:${s.type}:${s.shift_gb}`
            )
            .join(",")
        : "",
      defaultShiftCodeHash: defaultShiftCode?.length
        ? defaultShiftCode
            .map(
              (s) => `${s.shift_id}:${s.default_shift}:${s.type}:${s.shift_gb}`
            )
            .join(",")
        : "",
      // 전월 tail 데이터 변경 시에도 설정 위반 재검증이 필요함
      prevTailHash: prevTailData
        ? JSON.stringify({
            tail_days: prevTailData.tail_days,
            nurses: prevTailData.nurses?.map((n) => ({
              nurse_id: n.nurse_id,
              shifts: n.shifts,
            })),
          })
        : "none",
      resignationHash: memberStatusMap
        ? Array.from(memberStatusMap.entries())
            .map(
              ([nurseId, member]) => `${nurseId}:${member.resign_date ?? ""}`
            )
            .sort()
            .join(",")
        : "none",
    });

    if (prevValidationKey.current === validationKey) {
      setIsValidating(false);
      setPhase("done");
      return;
    }
    prevValidationKey.current = validationKey;

    clearDebounceTimer();
    const runId = ++validationRunIdRef.current;

    // 디바운싱: 300ms 후에 검증 수행
    setIsValidating(true);
    setPhase("scheduled");
    debounceTimerRef.current = setTimeout(() => {
      if (validationRunIdRef.current !== runId) {
        return;
      }
      setPhase("running");
      const useMidValue = (
        serverConfig as Record<string, unknown> | null | undefined
      )?.["use_mid"];
      const isUseMid =
        typeof useMidValue === "boolean" ? useMidValue : undefined;

      const manpowerResult = disableManpowerValidation
        ? { violations: [], detailed_violations: [] }
        : validateManpower(
            copyNurses,
            manPower_monthly,
            manPower_daily ?? null,
            year,
            month,
            defaultShiftCode,
            isExpandedRef.current, // ref를 사용하여 함수 재생성 방지
            shiftsData,
            isUseMid,
            useMaxManpower,
            enableMaxManpowerViolation,
            shiftCatalog
          );

      const configResult = validateConfigRules(
        copyNurses,
        serverConfig ?? null,
        year,
        month,
        defaultShiftCode,
        shiftsData,
        prevTailData,
        memberStatusMap
      );

      const result = {
        violations: [...manpowerResult.violations, ...configResult.violations],
        detailed_violations: [
          ...manpowerResult.detailed_violations,
          ...configResult.detailed_violations,
        ],
      };

      if (validationRunIdRef.current === runId) {
        setIsValidating(false);
        setValidateList(result);

        // 셀별 위반 매핑 프리컴퓨팅
        const precepteePol = getPrecepteePolicy(
          serverConfig as Record<string, unknown> | null | undefined
        );

        // cellViolationMap은 내부적으로 -tailStart ~ daysInMonth-1 범위를 사용하므로,
        // UI가 실제로 렌더링하는 전월 tail day 개수와 동일하게 정규화합니다.
        const tailDaysArr = (() => {
          const tailDaysFromPayload = prevTailData?.tail_days;
          if (
            Array.isArray(tailDaysFromPayload) &&
            tailDaysFromPayload.length > 0
          ) {
            return tailDaysFromPayload
              .map(Number)
              .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31)
              .sort((a, b) => a - b);
          }

          const firstPrevNurse = prevTailData?.nurses?.[0];
          const shifts = firstPrevNurse?.shifts;
          if (shifts) {
            return Object.keys(shifts)
              .map(Number)
              .filter((d) => Number.isFinite(d) && d >= 1 && d <= 31)
              .sort((a, b) => a - b);
          }

          return [] as number[];
        })();

        const normalizedTailDaysCount = tailDaysArr.length;

        const formatDayLabel = (idx?: number) => {
          if (typeof idx !== "number") return "-";
          if (idx < 0) {
            const tailIndex = tailDaysArr.length + idx;
            const tailDate = tailDaysArr[tailIndex];
            return tailDate
              ? `전월 ${tailDate}일`
              : `전월 ${Math.abs(idx)}일전`;
          }
          return `${idx + 1}일`;
        };

        const manpowerValidateShiftKeys =
          disableManpowerValidation || !manPower_monthly
            ? undefined
            : Object.keys(manPower_monthly).filter((k) => {
                if (typeof manPower_monthly[k] !== "number") return false;
                if (k.endsWith("_max")) return false;
                if (isUseMid === false && k === "M") return false;
                return true;
              });

        // availableShifts: defaultShiftCode + shiftsData를 id 기준으로 병합하여
        // buildCellViolationMap 내부의 buildShiftLookupMaps가 id 조회 가능하도록 전달
        const mergedAvailableShifts =
          shiftCatalog?.shifts ??
          mergeShiftsForCellLookup(
            defaultShiftCode ?? [],
            normalizeShiftsForCellLookup(shiftsData)
          );

        // inbound 잠금 정책: 간호사별 편집 불가 day 집합 계산
        const nurseLockedDaysMap: Map<string, Set<number>> | undefined =
          currentGroupId
            ? (() => {
                const m = new Map<string, Set<number>>();
                const dim = getDaysInMonth(year, month) || 30;
                copyNurses.forEach((n) => {
                  const nurseId =
                    n.nurse_id ||
                    ((n as Record<string, unknown>).id as string | undefined);
                  if (!nurseId) return;
                  const inbound = (n as Record<string, unknown>).inbound as
                    | import("@/hooks/roster-craete/useRosterSchedule").InboundInfo
                    | import("@/hooks/roster-craete/useRosterSchedule").InboundItem[]
                    | undefined;
                  const { lockedDays } = classifyInboundForGroup(
                    inbound,
                    currentGroupId,
                    dim,
                    year,
                    month
                  );
                  if (lockedDays.size > 0) {
                    m.set(nurseId, lockedDays);
                  }
                });
                return m.size > 0 ? m : undefined;
              })()
            : undefined;

        const cellMap = buildCellViolationMap(
          result.detailed_violations,
          copyNurses.map((n) => ({
            nurse_id: n.nurse_id,
            id: (n as Record<string, unknown>).id as string | undefined,
            name: n.name,
            schedule:
              n.schedule?.map((cell) => normalizeRosterScheduleValue(cell)) ??
              null,
            schedule_ids:
              (n as { schedule_ids?: (number | null)[] | null }).schedule_ids ??
              null,
            preceptor: n.preceptor ? { role: n.preceptor.role } : null,
          })),
          {
            availableShifts: mergedAvailableShifts.map((s) => ({
              shift_id: s.shift_id,
              default_shift: s.default_shift ?? null,
              shift_gb: s.shift_gb,
              id: s.id,
            })),
            shiftCatalog,
            disableManpowerValidation,
            precepteePolicy: {
              excludePrecepteeFromUi: precepteePol.excludePrecepteeFromUi,
              excludePrecepteeFromCount: precepteePol.excludePrecepteeFromCount,
            },
            tailDaysCount: normalizedTailDaysCount,
            formatDayLabel,
            nurseLockedDaysMap,
            ...(manpowerValidateShiftKeys &&
            manpowerValidateShiftKeys.length > 0
              ? {
                  manpowerValidateShiftKeys,
                  includeMidInManpowerGroupMap: isUseMid !== false,
                }
              : {}),
          }
        );
        setCellViolationMap(cellMap);

        setPhase("done");
      }
    }, 300); // 300ms 디바운스
  }, [
    copyNurses,
    currentScheduleID,
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    enableMaxManpowerViolation,
    year,
    month,
    currentScheduleInfo,
    isDragEditing,
    serverConfig,
    defaultShiftCode,
    // isExpanded는 ref로 관리하므로 dependency에서 제외
    shiftsData,
    shiftCatalog,
    disableManpowerValidation,
    prevTailData,
    generateRosterHash,
    currentGroupId,
    memberStatusMap,
  ]);

  // scheduleId 또는 날짜 변경 시 즉시 위반사항 초기화
  useEffect(() => {
    const currentDateKey = currentScheduleInfo
      ? `${currentScheduleInfo.year}-${currentScheduleInfo.month}`
      : "";
    const scheduleIdChanged = prevScheduleId.current !== currentScheduleID;
    const dateChanged = prevDateKey.current !== currentDateKey;

    if (scheduleIdChanged || dateChanged) {
      setValidateList(null);
      setCellViolationMap((prev) => (prev.size === 0 ? prev : new Map())); // 이전 월의 셀별 위반도 함께 초기화
      setPhase("idle");
      prevValidationKey.current = "";
      prevScheduleId.current = currentScheduleID;
      prevDateKey.current = currentDateKey;
    }
  }, [currentScheduleID, currentScheduleInfo]);

  // 컴포넌트 언마운트 시 디바운스 타이머 정리
  useEffect(() => {
    return () => {
      clearDebounceTimer();
      validationRunIdRef.current += 1;
      setPhase("idle");
    };
  }, []);

  return {
    validateList,
    cellViolationMap,
    isValidating,
    phase,
    validate: executeValidation,
  };
}
